// Cột phải Bảng tin: sinh nhật & kỷ niệm, cuộc họp / lịch làm việc, truy cập nhanh.
// Chưa nối API: đồng nghiệp là dữ liệu mẫu; cuộc họp, lời chúc đã gửi, lối tắt đã chọn lưu ở trình duyệt theo từng tài khoản.
import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

// ---------------------------------------------------------------------------
// Sinh nhật & kỷ niệm
// ---------------------------------------------------------------------------
export interface Colleague { id: string; name: string; position: string; department: string; birthday: string; joinDate: string; avatar?: string | null }

/** Ngày sinh / ngày vào làm tính theo hôm nay để dữ liệu mẫu luôn có người sắp sinh nhật */
const rel = (days: number, year: number) => dayjs().add(days, 'day').year(year).format('YYYY-MM-DD');
export const COLLEAGUES: Colleague[] = [
  { id: 'c1', name: 'Trần Thị Kim Loan', position: 'Kỹ thuật viên', department: 'Phòng Dịch vụ Chăm sóc', birthday: rel(0, 1996), joinDate: '2023-04-10' },
  { id: 'c2', name: 'Nguyễn Duy Kiên', position: 'Tư vấn viên', department: 'Phòng Tư vấn Thẩm mỹ', birthday: rel(2, 1994), joinDate: '2024-01-15' },
  { id: 'c3', name: 'Lê Thị Bảo Trâm', position: 'Lễ tân', department: 'Sale cơ sở', birthday: rel(5, 1999), joinDate: rel(1, 2023) },
  { id: 'c4', name: 'Phạm Minh Đức', position: 'Telesale', department: 'Phòng Kinh doanh', birthday: rel(12, 1997), joinDate: rel(0, 2021) },
  { id: 'c5', name: 'Võ Thanh Hà', position: 'Trưởng nhóm Marketing', department: 'Phòng Marketing', birthday: rel(25, 1990), joinDate: rel(4, 2020) },
  { id: 'c6', name: 'Đặng Quốc Huy', position: 'Content Creator', department: 'Phòng Marketing', birthday: rel(-3, 1998), joinDate: '2025-06-01' },
];

export interface Occasion { person: Colleague; kind: 'birthday' | 'anniversary'; date: Dayjs; daysUntil: number; years: number }

/** Lần tới của một ngày-tháng (29/02 rơi vào năm không nhuận thì lấy 28/02) */
export function nextOccurrence(iso: string, today: Dayjs): Dayjs {
  const d = dayjs(iso);
  const at = (y: number) => {
    const base = dayjs(`${y}-${String(d.month() + 1).padStart(2, '0')}-01`);
    return base.date(Math.min(d.date(), base.daysInMonth()));
  };
  const t = today.startOf('day');
  const thisYear = at(t.year());
  return thisYear.isBefore(t) ? at(t.year() + 1) : thisYear;
}

/** Sinh nhật và kỷ niệm ngày vào làm (từ 1 năm) trong `days` ngày tới, gần nhất trước */
export function upcomingOccasions(people: Colleague[], today: Dayjs, days = 7): Occasion[] {
  const t = today.startOf('day');
  const out: Occasion[] = [];
  people.forEach(p => {
    const b = nextOccurrence(p.birthday, t);
    out.push({ person: p, kind: 'birthday', date: b, daysUntil: b.diff(t, 'day'), years: b.year() - dayjs(p.birthday).year() });
    const a = nextOccurrence(p.joinDate, t);
    const years = a.year() - dayjs(p.joinDate).year();
    if (years >= 1) out.push({ person: p, kind: 'anniversary', date: a, daysUntil: a.diff(t, 'day'), years });
  });
  return out.filter(o => o.daysUntil <= days).sort((a, b) => a.daysUntil - b.daysUntil || a.kind.localeCompare(b.kind) || a.person.name.localeCompare(b.person.name));
}

export const whenLabel = (d: number) => (d === 0 ? 'Hôm nay' : d === 1 ? 'Ngày mai' : `Còn ${d} ngày`);

// ---------------------------------------------------------------------------
// Lưu trữ theo tài khoản + đồng bộ giữa các widget
// ---------------------------------------------------------------------------
const EVENT = 'social-right-panel';
function useStored<T>(key: string, fallback: () => T): [T, (v: T) => void] {
  const read = useCallback((): T => {
    try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback(); } catch { return fallback(); }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const [value, setValue] = useState<T>(read);
  useEffect(() => {
    const sync = () => setValue(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener('storage', sync); };
  }, [read]);
  const save = useCallback((v: T) => {
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* bộ nhớ đầy: bỏ qua */ }
    window.dispatchEvent(new Event(EVENT));
  }, [key]);
  return [value, save];
}

/** Lời chúc đã gửi trong năm nay (khóa: id người + loại + năm) */
export function useWishesSent(meId: string) {
  const [sent, setSent] = useStored<string[]>(`social_wishes_${meId}`, () => []);
  const keyOf = (o: Occasion) => `${o.person.id}-${o.kind}-${o.date.year()}`;
  return {
    isSent: (o: Occasion) => sent.includes(keyOf(o)),
    markSent: (o: Occasion) => { if (!sent.includes(keyOf(o))) setSent([...sent, keyOf(o)]); },
  };
}

// ---------------------------------------------------------------------------
// Cuộc họp / lịch làm việc
// ---------------------------------------------------------------------------
export interface Meeting { id: string; title: string; start: string; end: string; link?: string; location?: string; attendees: string[]; note?: string; createdBy: string }

const seedMeetings = (): Meeting[] => {
  const d = dayjs().startOf('day');
  return [
    { id: 'm-seed-1', title: 'Weekly Update – Khơ Thị', start: d.hour(10).toISOString(), end: d.hour(11).toISOString(), link: 'https://meet.google.com/', attendees: ['Ban Giám đốc', 'Trưởng bộ phận'], createdBy: 'system' },
    { id: 'm-seed-2', title: 'Giao ban chi nhánh Thảo Điền', start: d.add(1, 'day').hour(8).minute(30).toISOString(), end: d.add(1, 'day').hour(9).toISOString(), location: 'Phòng họp tầng 2', attendees: ['Sale cơ sở'], createdBy: 'system' },
  ];
};

export type MeetingState = 'live' | 'soon' | 'upcoming' | 'past';
export function meetingState(m: Meeting, now: Dayjs): MeetingState {
  if (now.isAfter(dayjs(m.end))) return 'past';
  if (!now.isBefore(dayjs(m.start))) return 'live';
  return dayjs(m.start).diff(now, 'minute') <= 30 ? 'soon' : 'upcoming';
}

/** Cuộc họp chưa kết thúc trong `days` ngày tới, sớm nhất trước */
export const agendaOf = (list: Meeting[], now: Dayjs, days = 7) =>
  list.filter(m => meetingState(m, now) !== 'past' && dayjs(m.start).isBefore(now.add(days, 'day').endOf('day')))
    .sort((a, b) => a.start.localeCompare(b.start));

export function useMeetings(meId: string) {
  const [list, setList] = useStored<Meeting[]>(`social_meetings_${meId}`, seedMeetings);
  return {
    list,
    add: (m: Omit<Meeting, 'id'>) => setList([...list, { ...m, id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}` }]),
    remove: (id: string) => setList(list.filter(m => m.id !== id)),
  };
}

// ---------------------------------------------------------------------------
// Truy cập nhanh
// ---------------------------------------------------------------------------
export const QUICK_LINKS = [
  { key: 'sales-dashboard', label: 'Dashboard kinh doanh', desc: 'Trực page · Telesale · Sale cơ sở', path: '/dashbroad-truc-page', color: 'bg-indigo-500' },
  { key: 'customers', label: 'Khách hàng', desc: 'Danh sách & chăm sóc khách', path: '/customer', color: 'bg-emerald-500' },
  { key: 'appointments', label: 'Lịch hẹn', desc: 'Lịch hẹn khách tại cơ sở', path: '/appointments', color: 'bg-sky-500' },
  { key: 'chats', label: 'Tin nhắn', desc: 'Chat với khách hàng', path: '/chats', color: 'bg-blue-500' },
  { key: 'marketing', label: 'Báo cáo Marketing', desc: 'Chi phí & hiệu quả quảng cáo', path: '/marketing-report', color: 'bg-rose-500' },
  { key: 'documents', label: 'Tài liệu', desc: 'Quy trình, biểu mẫu nội bộ', path: '/list-of-documents', color: 'bg-amber-500' },
  { key: 'orders', label: 'Đơn hàng', desc: 'Danh sách đơn hàng', path: '/list-order', color: 'bg-purple-500' },
];
const DEFAULT_LINKS = ['sales-dashboard', 'customers', 'appointments', 'documents'];

export function useQuickLinks(meId: string) {
  const [keys, setKeys] = useStored<string[]>(`social_quick_links_${meId}`, () => DEFAULT_LINKS);
  return { keys, setKeys, links: keys.map(k => QUICK_LINKS.find(l => l.key === k)).filter(Boolean) as typeof QUICK_LINKS };
}
